import type { ScalingMode } from '../types/UserMonitorConfig';
import type { ScalingPreset } from '../types/Monitor';
import { SCALING_FACTORS } from '../constants';
import { calculateLogicalResolution } from './ppiCalculator';

/**
 * Returns the approximate Apple HiDPI scaling factor for the given generic mode.
 * @deprecated Prefer getGenericScalingPresets or per-monitor ScalingPreset[].
 */
export function getScalingFactor(mode: ScalingMode): number {
  return SCALING_FACTORS[mode];
}

/**
 * Returns the logical resolution (in points) for a monitor operating in a
 * given generic Apple display scaling mode.
 * @deprecated Prefer getGenericScalingPresets or per-monitor ScalingPreset[].
 */
export function getLogicalResolutionForMode(
  nativeW: number,
  nativeH: number,
  mode: ScalingMode
): { logicalW: number; logicalH: number } {
  const factor = getScalingFactor(mode);
  return calculateLogicalResolution(nativeW, nativeH, factor);
}

/**
 * Builds a generic 4-preset list for monitors that do not have Apple-specified
 * per-model scaling presets (e.g. standard 4K monitors from third-party brands).
 * Uses the approximate 1× / 1.5× / 2× / 2.5× factors.
 */
export function getGenericScalingPresets(nativeW: number, nativeH: number): ScalingPreset[] {
  return [
    {
      label: 'Native',
      logicalW: nativeW,
      logicalH: nativeH,
    },
    {
      label: 'More Space',
      logicalW: Math.round(nativeW / SCALING_FACTORS.moreSpace),
      logicalH: Math.round(nativeH / SCALING_FACTORS.moreSpace),
    },
    {
      label: 'Default',
      logicalW: Math.round(nativeW / SCALING_FACTORS.default),
      logicalH: Math.round(nativeH / SCALING_FACTORS.default),
      isDefault: true,
    },
    {
      label: 'Larger Text',
      logicalW: Math.round(nativeW / SCALING_FACTORS.moreText),
      logicalH: Math.round(nativeH / SCALING_FACTORS.moreText),
    },
  ];
}

/**
 * Returns the scaling presets to display for a monitor.
 * Uses the monitor's own `scalingPresets` if defined; otherwise falls back
 * to the generic 4-preset list derived from the native resolution.
 */
export function getPresetsForMonitor(
  monitor: { nativeResolutionW: number; nativeResolutionH: number; scalingPresets?: ScalingPreset[] }
): ScalingPreset[] {
  if (monitor.scalingPresets && monitor.scalingPresets.length > 0) {
    return monitor.scalingPresets;
  }
  return getGenericScalingPresets(monitor.nativeResolutionW, monitor.nativeResolutionH);
}

/**
 * Returns the default preset for a monitor — the one marked `isDefault`, or
 * the middle preset if none is marked.
 */
export function getDefaultPreset(presets: ScalingPreset[]): ScalingPreset {
  return presets.find((p) => p.isDefault) ?? presets[Math.floor(presets.length / 2)];
}

/**
 * @deprecated Use getGenericScalingPresets instead.
 */
export function getAllScalingPresets(
  nativeW: number,
  nativeH: number
): Record<ScalingMode, { logicalW: number; logicalH: number }> {
  const modes: ScalingMode[] = ['noScaling', 'moreSpace', 'default', 'moreText'];
  return Object.fromEntries(
    modes.map((mode) => [mode, getLogicalResolutionForMode(nativeW, nativeH, mode)])
  ) as Record<ScalingMode, { logicalW: number; logicalH: number }>;
}
